import { useState } from "react"
import { Stack } from "@mui/material"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { enqueueSnackbar } from "notistack"
import { TimePicker } from "@mui/x-date-pickers/TimePicker"
import { TimelineCalendarEventTimeRange } from "../../../createOrder/components/resource/calendar/fullCalendarTypes"
import { useAppLocalization } from "../../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { BookingCustomFieldsGroup } from "../../../createOrder/components/form/formTypes"
import { useDateTime } from "../../../../../modules/localization/useDateTime"

const useStyles = makeStyles()((_theme) => ({
    pickerList: {
        width: "70px",
    },
}))

type TimeRangePickerProps = {
    timeRange: TimelineCalendarEventTimeRange
}

export function TimeRangePicker({ timeRange }: TimeRangePickerProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const [{ value: startDateTime }, , startHelpers] =
        useField<BookingCustomFieldsGroup["startDateTime"]>("startDateTime")
    const [{ value: endDateTime }, , endHelpers] = useField<BookingCustomFieldsGroup["endDateTime"]>("endDateTime")

    const minTime = dateTimeFactory.fromISO(timeRange.start)
    const maxTime = dateTimeFactory.fromISO(timeRange.end)

    const startTime = dateTimeFactory.fromISO(startDateTime)
    const endTime = dateTimeFactory.fromISO(endDateTime)

    const handleShowErrorSnackbar = () => {
        enqueueSnackbar({
            variant: "error",
            message: l10n.getString("create-order-confirm-booking-time-range-picker-error"),
        })
    }

    const [openFrom, setOpenFrom] = useState(false)
    const [openTo, setOpenTo] = useState(false)

    return (
        <Stack flexDirection="row" alignItems="center" gap="1rem" width="60%">
            <LocalizedStrict id="create-order-confirm-booking-time-range-picker-from-field" attrs={{ label: true }}>
                <TimePicker
                    value={startTime}
                    onChange={(newTime) => {
                        if (newTime && newTime >= minTime && newTime < maxTime) {
                            let adjustedEndTime = endTime

                            if (!endTime || newTime >= endTime) {
                                adjustedEndTime = newTime.plus({ minutes: 15 })

                                if (adjustedEndTime > maxTime) {
                                    adjustedEndTime = maxTime
                                }

                                endHelpers.setValue(adjustedEndTime.toISO())
                            }

                            startHelpers.setValue(newTime.toISO())
                        } else {
                            handleShowErrorSnackbar()
                            newTime && startHelpers.setValue(newTime.toISO())
                        }
                    }}
                    label="From time:"
                    minTime={minTime}
                    maxTime={maxTime.minus({ minutes: 5 })}
                    ampm={false}
                    open={openFrom}
                    onOpen={() => setOpenFrom(true)}
                    onClose={() => setOpenFrom(false)}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            variant: "outlined",
                            onClick: () => setOpenFrom(true),
                        },
                        popper: {
                            sx: {
                                "& .MuiList-root": classes.pickerList,
                            },
                        },
                    }}
                />
            </LocalizedStrict>

            <LocalizedStrict id="create-order-confirm-booking-time-range-picker-to-field" attrs={{ label: true }}>
                <TimePicker
                    value={endTime}
                    onChange={(newTime) => {
                        if (newTime && newTime > startTime.plus({ minutes: 5 }) && newTime <= maxTime) {
                            endHelpers.setValue(newTime.toISO())
                        } else {
                            handleShowErrorSnackbar()
                            newTime && endHelpers.setValue(newTime.toISO())
                        }
                    }}
                    label="To time:"
                    minTime={startTime.plus({ minutes: 5 })}
                    maxTime={maxTime}
                    ampm={false}
                    open={openTo}
                    onOpen={() => setOpenTo(true)}
                    onClose={() => setOpenTo(false)}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            variant: "outlined",
                            onClick: () => setOpenTo(true),
                        },
                        popper: {
                            sx: {
                                "& .MuiList-root": classes.pickerList,
                            },
                        },
                    }}
                />
            </LocalizedStrict>
        </Stack>
    )
}
